import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit3, Trash2, BookOpen } from 'lucide-react';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyEmptyState,
  TaskyFormGroup,
  TaskyInput,
  TaskySelect,
  TaskySpinner,
  TaskyTextarea,
  useConfirm,
} from '../tasky';
import axiosInstance from '../../services/api/axiosConfig';
import { MangaStyleType } from '../../services/api/mangaGenerationService';

interface MangaStyleFormData {
  name: string;
  description: string;
  story_prompt_template: string;
  visual_style: string;
  sort_order: number;
  is_active: boolean;
}

const defaultFormData: MangaStyleFormData = {
  name: '',
  description: '',
  story_prompt_template: '',
  visual_style: 'modern',
  sort_order: 0,
  is_active: true,
};

const MangaStyleManagement: React.FC = () => {
  const confirm = useConfirm();
  const [styles, setStyles] = useState<MangaStyleType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<MangaStyleFormData>(defaultFormData);
  const [error, setError] = useState<string | null>(null);

  const fetchStyles = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get('/admin/manga-styles');
      if (response.data.success) {
        setStyles(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch manga styles:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStyles();
  }, [fetchStyles]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      if (editingId) {
        await axiosInstance.put(`/admin/manga-styles/${editingId}`, formData);
      } else {
        await axiosInstance.post('/admin/manga-styles', formData);
      }
      setShowForm(false);
      setEditingId(null);
      setFormData(defaultFormData);
      fetchStyles();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save style');
    }
  };

  const handleEdit = (style: MangaStyleType) => {
    setEditingId(style.id);
    setFormData({
      name: style.name,
      description: style.description,
      story_prompt_template: style.story_prompt_template,
      visual_style: style.visual_style,
      sort_order: style.sort_order,
      is_active: style.is_active,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!(await confirm({ title: 'このスタイルを削除しますか？', tone: 'destructive', confirmText: '削除する' }))) return;
    try {
      await axiosInstance.delete(`/admin/manga-styles/${id}`);
      fetchStyles();
    } catch (err) {
      console.error('Failed to delete style:', err);
    }
  };

  const handleToggle = async (id: number) => {
    try {
      await axiosInstance.put(`/admin/manga-styles/${id}/toggle`);
      fetchStyles();
    } catch (err) {
      console.error('Failed to toggle style:', err);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(defaultFormData);
    setError(null);
  };

  if (isLoading && styles.length === 0) {
    return (
      <div className="flex justify-center p-10">
        <TaskySpinner size="md" />
      </div>
    );
  }

  return (
    <div className="p-5">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold text-dark m-0">漫画スタイル管理</h3>
          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-warm-surface text-gray-600">
            {styles.length}件
          </span>
        </div>
        <TaskyButton
          size="sm"
          onClick={() => { setShowForm(true); setEditingId(null); setFormData(defaultFormData); }}
        >
          <Plus size={16} /> 新規追加
        </TaskyButton>
      </div>

      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="bg-warm-bg border border-warm-border rounded-tasky-md p-5 mb-5"
        >
          <h4 className="text-sm font-semibold text-dark mt-0 mb-4">
            {editingId ? 'スタイルを編集' : 'スタイルを追加'}
          </h4>
          {error && <TaskyAlert tone="error" className="mb-3">{error}</TaskyAlert>}

          <div className="grid gap-3">
            <TaskyFormGroup label="名前" htmlFor="manga-style-name">
              <TaskyInput
                id="manga-style-name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </TaskyFormGroup>

            <TaskyFormGroup label="説明" htmlFor="manga-style-description">
              <TaskyTextarea
                id="manga-style-description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                required
                rows={2}
              />
            </TaskyFormGroup>

            <TaskyFormGroup label="ストーリープロンプトテンプレート" htmlFor="manga-style-prompt">
              <TaskyTextarea
                id="manga-style-prompt"
                value={formData.story_prompt_template}
                onChange={(e) => setFormData(prev => ({ ...prev, story_prompt_template: e.target.value }))}
                required
                rows={5}
              />
            </TaskyFormGroup>

            <div className="grid grid-cols-2 gap-3">
              <TaskyFormGroup label="ビジュアルスタイル" htmlFor="manga-style-visual">
                <TaskySelect
                  id="manga-style-visual"
                  value={formData.visual_style}
                  onChange={(e) => setFormData(prev => ({ ...prev, visual_style: e.target.value }))}
                >
                  <option value="modern">Modern</option>
                  <option value="classic">Classic</option>
                  <option value="pop">Pop</option>
                  <option value="business">Business</option>
                </TaskySelect>
              </TaskyFormGroup>

              <TaskyFormGroup label="表示順" htmlFor="manga-style-sort">
                <TaskyInput
                  id="manga-style-sort"
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                />
              </TaskyFormGroup>
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <TaskyButton type="submit" size="sm">
              {editingId ? '更新' : '追加'}
            </TaskyButton>
            <TaskyButton type="button" variant="outline" size="sm" onClick={handleCancel}>
              キャンセル
            </TaskyButton>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {styles.map(style => (
          <div
            key={style.id}
            className="bg-white border border-warm-border rounded-tasky-md overflow-hidden hover:shadow-md transition-shadow"
          >
            <div className="h-32 bg-warm-surface flex items-center justify-center">
              <BookOpen size={48} className="text-gray-400" />
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-2 mb-1">
                <h4 className="font-medium text-dark m-0">{style.name}</h4>
                <div className="flex items-center gap-1.5 shrink-0">
                  <TaskyBadge variant="warm">{style.visual_style}</TaskyBadge>
                  <span className="text-xs text-gray-400">順序 {style.sort_order}</span>
                </div>
              </div>
              <p className="text-sm text-gray-500 mb-3 line-clamp-2">{style.description}</p>
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => handleToggle(style.id)}
                  className="flex items-center gap-2 cursor-pointer bg-transparent border-none p-0"
                  aria-label={style.is_active ? '無効にする' : '有効にする'}
                >
                  <span
                    className={`relative inline-block w-9 h-5 rounded-full transition-colors ${
                      style.is_active ? 'bg-primary' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
                        style.is_active ? 'translate-x-4' : ''
                      }`}
                    />
                  </span>
                  <span className="text-xs text-gray-500">{style.is_active ? '有効' : '無効'}</span>
                </button>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => handleEdit(style)}
                    aria-label="編集"
                    className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-warm-hover hover:text-gray-600 transition-colors"
                  >
                    <Edit3 size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(style.id)}
                    aria-label="削除"
                    className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-warm-hover hover:text-destructive transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {styles.length === 0 && !isLoading && (
        <TaskyEmptyState title="スタイルがありません" className="mt-4" />
      )}
    </div>
  );
};

export default MangaStyleManagement;
